import {
  Box,
  BoxProps,
  Card,
  Text,
  Button,
  ProductPrice,
  PriceButton,
  Heading,
} from "@vinomofo/components";
import { CountdownTimer, calculateTimeLeft } from "./CountdownTimer";
import { Tiers, VinoUser } from "../../contexts/auth/state";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";

import { COLORS } from "../../styles";
import OfferCardImage from "./OfferCardImage";
import { BadgeStack } from "../badge-stack";
import { determineTierIndex } from "../../utils/loyalty";
import isBrowser from "../../utils/is-browser";
import { mockTrackEvent } from "../../utils/event-tracking";
import { navigate, Link } from "gatsby";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useLoyalty } from "../../hooks/account/use-loyalty";
import { OfferCardProps, isWhiskymofoEnabled, CardAnchor } from "./OfferCard";
import { discountDisplays } from "../../utils/constants";
import { EventSubType, EventType } from "../../types";

const TwoColumnCard = styled(Card)<{
  isSoldOut: boolean;
  isSpirit: boolean;
  bgImage: string;
  skeleton?: boolean;
}>`
  grid-column: span 2;
  padding: 15px;
  border-radius: 8px;

  @media ${({ theme }) => theme.mq.max.mobile} {
    padding: 8px;
  }

  * p,
  * h3,
  * h3,
  * img,
  * svg,
  * span,
  * button,
  * [class*="DiscountBadge__"] {
    filter: ${({ isSoldOut, isSpirit }) =>
      isSoldOut && isSpirit ? "grayscale(100%)" : "none"} !important;
    opacity: ${({ isSoldOut }) => (isSoldOut ? "0.75" : "1")};
  }

  &:after {
    content: "";
    top: -23px;
    z-index: 1;
    width: 100%;
    bottom: -23px;
    position: absolute;
    border-radius: 8px;
    height: calc(100% + 23px);

    ${({ bgImage, skeleton }) =>
      skeleton
        ? `
        background: white;
      `
        : bgImage
        ? `
            background: linear-gradient(rgb(32, 31, 25) 0%, rgb(127, 111, 88) 100%), url(${bgImage});
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
          `
        : `
            background: linear-gradient(rgb(32, 31, 25) 0%, rgb(127, 111, 88) 100%);
          `}
  }

  [class*="OfferCardImage__"] {
    max-width: 460px;
    margin-top: -24px;
    position: absolute;
    width: 100%;
    inset: 0px 0px 0px 20%;
    z-index: 1;
    min-height: 460px;
    filter: ${({ isSoldOut, isSpirit }) =>
      isSoldOut && isSpirit ? "grayscale(100%)" : "none"} !important;

    @media ${({ theme }) => theme.mq.max.tablet} {
      max-width: 390px;
      margin-top: -18px;
      inset: 0px 0px 0px 28%;
      min-height: 390px;
    }

    @media ${({ theme }) => theme.mq.min.tablet} and (max-width: 1024px) {
      max-width: 390px;
      margin-top: -20px;
      inset: 0px 0px 0px 18%;
      min-height: 390px;
    }

    @media ${({ theme }) => theme.mq.max.mobile} {
      max-width: 310px;
      margin-top: -18px;
      inset: 0px 0px 0px 15%;
      min-height: 310px;
    }

    @media (max-width: 932px) and ((orientation: landscape) or (min-aspect-ratio: 1/1)) {
      inset: 0px 0px 0px 22%;
    }
  }

  [class*="ProductRRP__"] {
    color: ${({ theme }) => theme.colors.divider};
    font-size: 25px;

    @media ${({ theme }) => theme.mq.max.mobile} {
      font-size: 20px;
    }
  }

  [class*="ProductPrice__"] {
    color: ${({ theme }) => theme.colors.white};
    font-size: 30px;

    @media ${({ theme }) => theme.mq.max.mobile} {
      font-size: 22px;
    }
  }
`;

export const OfferTimer = styled(CountdownTimer)<{
  isExpired: boolean;
  isSpirit: boolean;
  urgencyMessage?: boolean;
  isLessThan24Hours?: boolean;
}>`
  display: block;
  opacity: ${({ isExpired, isSpirit }) => (isExpired && !isSpirit ? 0.75 : 1)};
  ${({ isExpired }) => isExpired && "filter: grayscale(100%);"}
  border-radius: 0;

  > div:last-child {
    display: block;
  }

  [class*="TimeDisplay__Container"] {
    border-radius: 0;
    background: transparent;
    padding: 0;
    color: white;
    font-family: DM Sans, sans-serif;
    border: none;
    height: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2px;

    [class*="TimeDisplay__TimeLabel"] {
      display: none;
    }

    > div {
      max-width: 34px;
      text-align: center;
      position: relative;
      display: block;

      > div {
        width: 34px;
        position: relative;
        display: block;
        margin: auto;

        span {
          width: 100%;
          color: white;
          font-family: "DM Sans", sans-serif;
          font-size: 20px;
          display: inline-block;

          @media ${({ theme }) => theme.mq.max.mobile} {
            font-size: 16px;
          }
        }
      }

      &:after {
        content: ":";
        position: absolute;
        top: -2px;
        right: -2px;
        font-size: 18px;
      }

      &:last-child {
        &:after {
          display: none;
        }
      }
    }
  }
`;

export const HRLine = styled(Box)<{ skeleton?: boolean }>`
  ${({ skeleton }) =>
    skeleton
      ? `
      background: rgb(226, 226, 226);
    `
      : `
      border-bottom: 1px solid;
      border-image-source: linear-gradient(90deg, #7C6C56 0%, #999999 100%);
      background: linear-gradient(180deg, #201F19 0%, #7F6F58 100%);d: linear-gradient(rgb(32, 31, 25) 0%, rgb(127, 111, 88) 100%);
    `}
`;

export const StyledTitle = styled(Box)`
  @media ${({ theme }) => theme.mq.max.desktop} {
    min-height: 32px;
  }
`;

const StyledAddToCart = styled(Button)`
  z-index: 9;
  position: relative;

  &.btn-is-loading {
    color: rgba(255, 255, 255, 0);
    background-color: #48a90f;
    border-color: #48a90f;
    transition: all 0.5s;
    &:hover {
      color: transparent;
    }
    &::before {
      content: "Loading ...";
      color: rgba(255, 255, 255, 0.7);
      position: absolute;
      left: 0;
      right: 0;
      animation: moveUp 0.5s;
    }
  }
`;

export const StyledTags = styled(Button)`
  position: relative;
  border-style: solid;
  border-width: 1px;
  margin-bottom: 5px;
  font-family: "DM Sans", sans-serif;
`;

export const StyledBadgeStack = styled(BadgeStack)<{ columns: number }>`
  [class*="Badge__ImageContainer"] {
    height: 100%;
  }
`;

export const TwoColumnOfferCard = (
  props: OfferCardProps & BoxProps
): JSX.Element => {
  const {
    productId,
    awards,
    discountPercentage,
    discountDollarValue,
    discountDisplay,
    price,
    unitRRP,
    packRRP,
    endsAt,
    showPriceDrop,
    showCountdown,
    showAwardBadges,
    hideRRP,
    hideVIMExclusive,
    priceDescription,
    region,
    horizontalBadges,
    roundelsImage,
    cardImage,
    urgencyMessage,
    showDiscountBadge,
    isMixed = false,
    isSoldOut = false,
    skeleton = false,
    variant = "responsive",
    trackEvent = mockTrackEvent,
    backgroundColor,
    eventPage,
    route,
    name,
    offerType,
    sku,
    is_wine,
    isSpirit,
    wideHeroImage,
    detailsImageWide,
    moreBtnLink,
    disableAllDisplayFlags = false,
    secret,
    varietal,
    category,
    isRecommended,
    columns,
    tiersEligible,
    styles,
    bottleShape,
    tags,
    offerJson,
    isMyOffer,
    queryID,
    productCardLayout,
    cardBackgroundImage,
    indexId,
    ...rest
  } = props;

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(new Date(endsAt)));
  const isExpired = endsAt ? timeLeft === null : false;

  const { loyalty, showLoyalty } = useLoyalty();
  const { addItem } = useCart();
  const { user, favourites, fetchingFavourites } = useAuth();
  const userAppMeta = user?.["https://www.vinomofo.com/app_metadata"];
  const [productLoading, setProductLoading] = useState({});
  const [isTierEligible, setIsTierEligible] = useState(false);
  const customerId =
    userAppMeta?.[`BIGCOMMERCE_${process.env.GATSBY_VM_MARKET}_CUSTOMER_ID`];

  const favouriteUserId_local = isBrowser()
    ? localStorage.getItem("favouriteId")
    : null;

  const favouriteUserId = useMemo(() => {
    return user?.email ? user.email : favouriteUserId_local;
  }, [user?.email, favouriteUserId_local]);

  const favouriteKey = useMemo(() => {
    return `${favouriteUserId}_KEY_${offerJson?.objectID || offerJson?.id}`;
  }, [favouriteUserId, offerJson, isMyOffer, favourites]);

  const [isInFavourites, setIsInFavourites] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(false);

  useEffect(() => {
    if (favourites !== undefined && !fetchingFavourites) {
      const ticked = favourites?.some((item) => item.id === favouriteKey);
      setIsInFavourites(ticked);
    }
  }, [favourites, fetchingFavourites, favouriteUserId]);

  useEffect(() => {
    if (user) {
      const { tier } = determineTierIndex(
        user as VinoUser,
        showLoyalty,
        loyalty?.loyalty_tier
      );

      const includesUserTier = tiersEligible?.includes(
        tier?.toUpperCase() as Tiers
      );
      const includesALLTier = tiersEligible?.includes("ALL" as Tiers);
      setIsTierEligible(
        includesUserTier && !includesALLTier && !hideVIMExclusive
      );
    }
  }, [loyalty, tiersEligible, user]);

  const handleAddButtonClick = (e) => {
    if (!isSoldOut) {
      setProductLoading((oldState) => ({ ...oldState, [productId]: true }));

      addItem(productId, props, customerId, "Offer Card", () => {
        setProductLoading((oldState) => ({ ...oldState, [productId]: false }));
      });
    } else {
      navigate(route?.path);
    }
  };

  const rrp = isMixed ? packRRP : unitRRP;
  const isLarge = variant === "md" || variant === "lg";
  const skeletonColor = "divider";
  const cardImageBackgroundColor = backgroundColor || "";

  useEffect(() => {
    setTimeLeft(calculateTimeLeft(new Date(endsAt)));
  }, [endsAt]);

  let priceButtonColor = "white";
  let priceButtonBGColor =
    urgencyMessage && COLORS[urgencyMessage.backgroundColor];

  if (isTierEligible) {
    priceButtonBGColor = COLORS.black;
    priceButtonColor = COLORS.mofoGreen;
  }

  const discountText =
    discountDisplay === discountDisplays.dollar
      ? `$${Math.round(discountDollarValue)}`
      : `${Math.round(discountPercentage)}%`;

  return (
    <TwoColumnCard
      data-key={offerJson.id}
      variant={isLarge ? "hard" : "default"}
      {...rest}
      backgroundColor="white"
      key={favouriteKey}
      isSoldOut={isSoldOut}
      isSpirit={isSpirit && isWhiskymofoEnabled}
      back
      bgImage={cardBackgroundImage}
      skeleton={skeleton}
    >
      <Box
        width="100%"
        borderRadius="5px 5px 0 0"
        position="relative"
        zIndex={2}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          mt="-18px"
          minHeight={"24px"}
        >
          {skeleton ? (
            <>
              <Box
                backgroundColor={skeletonColor}
                width="50%"
                height="25px"
              ></Box>
              <Box
                backgroundColor={skeletonColor}
                width="20%"
                height="25px"
              ></Box>
            </>
          ) : (
            <>
              <Box>
                {!isMyOffer && (
                  <Text
                    color="white"
                    fontSize={[1, 2, 2]}
                    fontFamily="DM Sans, sans-serif"
                  >
                    {indexId === 0 ? (
                      `NEW DEAL JUST DROPPED`
                    ) : isTierEligible ? (
                      <Box
                        mb={0}
                        as="span"
                        p="4px"
                        display="flex"
                        justifyContent="center"
                      >
                        <Heading
                          as="span"
                          variant="lg"
                          color="primary"
                          lineHeight="none"
                          fontWeight={900}
                          fontSize={[1, 2, 3]}
                        >
                          VIM
                        </Heading>
                        <Heading
                          as="span"
                          variant="lg"
                          color="white"
                          lineHeight="none"
                          fontWeight={100}
                          fontSize={[1, 2, 3]}
                        >
                          &nbsp; Exclusive
                        </Heading>
                      </Box>
                    ) : (
                      !disableAllDisplayFlags &&
                      urgencyMessage && (
                        <Heading
                          as="span"
                          variant="lg"
                          // color={urgencyMessage?.backgroundColor
                          //   ? COLORS[urgencyMessage.backgroundColor] ?? urgencyMessage.backgroundColor
                          //   : "white"}
                          color="white"
                          lineHeight="none"
                          fontWeight={100}
                          fontSize={[1, 2, 3]}
                          fontFamily="DM Sans, sans-serif"
                        >
                          {urgencyMessage.description}
                        </Heading>
                      )
                    )}
                  </Text>
                )}
              </Box>

              {!isMyOffer && showCountdown && !isSoldOut && endsAt && (
                <OfferTimer
                  isExpired={isExpired}
                  start={new Date()}
                  end={new Date(endsAt)}
                  countDays={true}
                  countHours={true}
                  showSecs={true}
                  height="34px"
                  showHorizontalBar={false}
                  isSpirit={isSpirit && isWhiskymofoEnabled}
                  urgencyMessage={!!urgencyMessage}
                  spacing="0 2px"
                  isLessThan24Hours={
                    !isNaN(timeLeft?.days) &&
                    !isNaN(timeLeft?.hrs) &&
                    !isNaN(timeLeft?.mins) &&
                    !isNaN(timeLeft?.secs) &&
                    timeLeft.days === 0
                  }
                />
              )}
            </>
          )}
        </Box>

        <HRLine
          width="100%"
          height="1.5px"
          mt="10px"
          skeleton={skeleton}
          opacity={
            showCountdown && endsAt ||
            (!disableAllDisplayFlags && urgencyMessage) ||
            isTierEligible ||
            indexId === 0
              ? 1
              : 0
          }
        />

        <Box
          display="flex"
          minHeight={["305px", "390px", "400px", "434px"]}
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          // overflow={["hidden", "auto", "auto"]}
        >
          <Box
            width={["45%", "46%", "48%", "50%"]}
            display="flex"
            flexDirection="column"
            zIndex={9}
            gridGap="15px"
          >
            {skeleton ? (
              <>
                <Box
                  backgroundColor={skeletonColor}
                  height="35px"
                  maxWidth={["80px", "110px"]}
                  borderRadius="8px"
                  mb="4px"
                ></Box>

                <Box
                  backgroundColor={skeletonColor}
                  width="100%"
                  height="35px"
                  mb="4px"
                ></Box>

                <Box
                  backgroundColor={skeletonColor}
                  width="90%"
                  height="35px"
                  mb="4px"
                ></Box>

                <Box
                  backgroundColor={skeletonColor}
                  width="65%"
                  height="50px"
                  mb="4px"
                ></Box>

                <Box
                  height={["44px", "48px"]}
                  backgroundColor={skeletonColor}
                  borderRadius="24px"
                  maxWidth={["150px", "185px"]}
                />
              </>
            ) : (
              <>
                {!isMyOffer && showDiscountBadge && (
                  <StyledTags
                    type="button"
                    variant="outline"
                    backgroundColor="transparent"
                    color="white"
                    borderColor="white"
                    padding={["6px 8px", "10px 12px"]}
                    fontSize={["8px", "11px"]}
                    width="fit-content"
                    textTransform="uppercase"
                    minWidth={["80px", "110px"]}
                  >
                    Save {discountText} RRP
                  </StyledTags>
                )}

                <StyledTitle>
                  <Text
                    as="h3"
                    lineHeight="none"
                    fontFamily={"Graphik"}
                    letterSpacing="tight"
                    color="white"
                    textAlign="left"
                    fontSize={[3, 4, 6, 8]}
                    fontWeight={500}
                  >
                    {!isMyOffer ? (
                      <CardAnchor
                        onClick={(event) => {
                          isRecommended
                            ? trackEvent("Recommended Product Clicked")
                            : trackEvent("Product Clicked");
                          if (!event.metaKey) {
                            event.preventDefault();
                            navigate(route?.path, {
                              state: { queryID },
                            });
                          }
                        }}
                        href={route?.path}
                        skeleton={skeleton}
                        color="white"
                      >
                        {name}
                      </CardAnchor>
                    ) : (
                      <>{name}</>
                    )}
                  </Text>
                </StyledTitle>

                <ProductPrice size={["md", "lg"]} price={price} rrp={rrp} />

                {!isMyOffer &&
                  (isSoldOut ? (
                    <Box maxWidth="210px" zIndex={9} position="relative">
                      <PriceButton
                        onClick={(e) => {
                          if (isRecommended)
                            trackEvent("Recommended Product Added", {
                              search_index: localStorage.getItem(
                                "algoliaIndexName"
                              ),
                              eventType: EventType.Conversion,
                              eventSubType: EventSubType.AddToCart,
                              queryID: queryID,
                            });
                          handleAddButtonClick(e);
                        }}
                        cta="Add"
                        size={["sm", "md"]}
                        fullPrice={!is_wine ? null : !hideRRP && rrp}
                        displayPrice={price}
                        isAvailable={!isSoldOut}
                        isLoading={productLoading[productId]}
                        ctaBackgroundColor={priceButtonBGColor}
                        color={priceButtonColor}
                        hoverColor="white"
                        notifyMeText={[
                          "Notify me",
                          "Notify me when available",
                          "Notify me when available",
                        ]}
                      />
                    </Box>
                  ) : (
                    <StyledAddToCart
                      type="button"
                      color="black"
                      fontSize={[1, 2]}
                      maxWidth={["150px", "185px"]}
                      variant="primary"
                      backgroundColor="white"
                      padding={["12px 16px", "16px 20px"]}
                      className={
                        productLoading[productId] ? "btn-is-loading" : ""
                      }
                      onClick={(e) => {
                        if (isRecommended)
                          trackEvent("Recommended Product Added", {
                            search_index: localStorage.getItem(
                              "algoliaIndexName"
                            ),
                            eventType: EventType.Conversion,
                            eventSubType: EventSubType.AddToCart,
                            queryID: queryID,
                          });
                        handleAddButtonClick(e);
                      }}
                    >
                      Add to Cart
                    </StyledAddToCart>
                  ))}
              </>
            )}
          </Box>

          <OfferCardImage
            isExpired={isExpired}
            useRelativeSize={false}
            image={cardImage}
            secret={secret}
            isSmallBottle={
              cardImage?.height > 120 &&
              bottleShape?.toLowerCase() === "small whisky"
            }
          />

          <Box
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            alignItems="flex-end"
            alignSelf="flex-start"
            pt="20px"
            zIndex={9}
          >
            {tags &&
              tags
                .filter((t, i) => i <= 2)
                .map((tag, ix) => {
                  return skeleton ? (
                    <Box
                      backgroundColor={skeletonColor}
                      height="35px"
                      maxWidth={["80px", "110px"]}
                      width="100%"
                      borderRadius="8px"
                      mb="4px"
                      key={`${tag}-${ix}`}
                    ></Box>
                  ) : (
                    <StyledTags
                      type="button"
                      variant="outline"
                      backgroundColor="transparent"
                      color="white"
                      borderColor="white"
                      padding={["6px 8px", "10px 12px"]}
                      fontSize={["8px", "11px"]}
                      width="fit-content"
                      textTransform="uppercase"
                      minWidth={["80px", "110px"]}
                      key={`${tag}-${ix}`}
                    >
                      {tag}
                    </StyledTags>
                  );
                })}
            {showAwardBadges && (
              <StyledBadgeStack
                marginLeft="5px"
                badges={awards}
                badgeSize={["60px", "75px"]}
                horizontal={horizontalBadges}
                hideCount
                zIndex={2}
                columns={4}
                pt="15px"
              />
            )}
          </Box>
        </Box>
      </Box>
    </TwoColumnCard>
  );
};
